import { LitElement, html, nothing, classMap } from 'lit';
import { escapeHtml, interpolate, formatMoney } from './local-cart-format.js';
import { snapQuantity } from './local-cart-pricing.js';

/**
 * Header-search quick-add (local cart only).
 *
 * The theme's predictive search fills its products panel with an HTML string built in custom.js. When the local
 * cart is active, custom.js asks this module for one <local-cart-search-row> per product instead of a link row.
 * The AX search (and the Shopify fallback) only provide title, handle, image and SKU, so each result set is
 * completed with one products-proxy lookup (vendor, EAN, contextual price, quantity rule, purchasability) that
 * `hydrate` hands to the rows as their `snapshot`.
 */

const products = new Map();

export function wrapQuickAddResults(rows, count, strings) {
  const template = count === 1 ? strings.resultsCountOne : strings.resultsCountOther;
  return `<ul class="predictive-search__list" role="list">${rows}</ul><div class="op-quick-add__footer">${interpolate(template, { count })}</div>`;
}

function renderRowTag(product) {
  products.set(product.key, product);
  return `<li class="predictive-search__item" role="option"><local-cart-search-row class="op-quick-add is-pending" data-key="${escapeHtml(product.key)}"></local-cart-search-row></li>`;
}

async function hydrate(panel, lookup, { signal, isStale = () => false } = {}) {
  let variants = {};
  try {
    ({ variants } = await window.LocalCart.api.fetchVariants({ ...lookup, signal }));
  } catch (error) {
    if (error?.name === 'AbortError' || isStale()) return;
    console.error(error); // eslint-disable-line
  }
  if (isStale()) return;
  panel.querySelectorAll('local-cart-search-row').forEach((row) => {
    row.snapshot = variants[row.dataset.key] || null;
    row.hydrated = true;
  });
}

class LocalCartSearchRow extends LitElement {
  static properties = {
    snapshot: { attribute: false },
    hydrated: { state: true },
    quantity: { state: true },
    note: { state: true },
    busy: { state: true },
    added: { state: true }
  };

  constructor() {
    super();
    this.snapshot = null;
    this.hydrated = false;
    this.quantity = 1;
    this.note = '';
    this.busy = false;
    this.added = false;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.product = products.get(this.dataset.key) || {};
    this.strings = window.theme.strings.opSearch;
    this.atcStrings = window.theme.strings.localCart.atc;
  }

  willUpdate(changed) {
    if (changed.has('snapshot') && this.snapshot) this.quantity = this.snapshot.quantityRule?.min || 1;
  }

  updated() {
    this.classList.toggle('is-pending', !this.hydrated);
    this.classList.toggle('is-unavailable', this.hydrated && !this.orderable);
  }

  get orderable() {
    return Boolean(this.snapshot && window.LocalCart.isOrderable(this.snapshot));
  }

  get unavailableReason() {
    if (!this.hydrated || this.orderable) return '';
    if (!this.snapshot) return this.atcStrings.variant_missing;
    return this.snapshot.available === false ? this.atcStrings.sold_out : this.atcStrings.not_orderable;
  }

  get priceText() {
    if (!this.snapshot) return this.product.priceHtml || '';
    const unitPrice = window.LocalCart.effectiveTier(this.snapshot, Math.max(1, this.quantity)).price;
    if (unitPrice === 0 && window.theme?.settings?.hideZeroPrice) return '';
    return formatMoney(unitPrice);
  }

  handleQuantityInput(event) {
    this.quantity = parseInt(event.target.value, 10) || 1;
  }

  // Same rule as the cart page: below minimum becomes minimum, otherwise round down to the colli step.
  handleQuantityChange(event) {
    const snapped = snapQuantity(event.target.value, this.snapshot?.quantityRule);
    event.target.value = String(snapped);
    this.quantity = snapped;
  }

  handleQuantityKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.add();
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.stopPropagation();
    }
  }

  async add() {
    if (!this.orderable || this.busy) return;
    const formData = new FormData();
    formData.set('id', this.snapshot.variantId);
    const input = this.querySelector('.op-quick-add__qty input');
    const snapped = snapQuantity(input?.value ?? this.quantity, this.snapshot.quantityRule);
    if (input) input.value = String(snapped);
    this.quantity = snapped;
    formData.set('quantity', String(snapped));
    this.busy = true;
    this.note = '';
    try {
      const result = await window.LocalCart.addToCart(formData);
      window.LocalCart.showToast(this.atcStrings.added, { actionLabel: this.atcStrings.view_cart, actionHref: window.theme.routes.cart });
      if (result.adjustments.length) this.note = interpolate(this.atcStrings.adjusted, { quantity: result.newQuantity });
      this.added = true;
      setTimeout(() => { this.added = false; }, 1400);
    } catch (error) {
      const template = this.atcStrings[error.code] || window.theme.strings.cartError;
      this.note = interpolate(template, { quantity: error.details?.quantity ?? '', increment: error.details?.increment ?? '' });
    } finally {
      this.busy = false;
    }
  }

  render() {
    const { product, strings } = this;
    const url = `/products/${product.handle}`;
    const rule = this.snapshot?.quantityRule || { min: 1, increment: 1, max: null };
    const disabled = !this.orderable || this.busy;
    const reason = this.unavailableReason;
    return html`
      <a class="op-quick-add__media js-search-link" href=${url} tabindex="-1" aria-hidden="true">
        ${product.image ? html`<img src=${product.image} alt="" loading="lazy">` : html`<span>${product.sku || ''}</span>`}
      </a>
      <div class="op-quick-add__copy">
        <a class="op-quick-add__title focus-inset" href=${url}>${product.title}</a>
        ${this.snapshot?.vendor ? html`<span class="op-quick-add__vendor">${this.snapshot.vendor}</span>` : nothing}
        <div class="op-quick-add__meta">
          ${product.sku ? html`<span><b>${strings.skuLabel}</b> ${product.sku}</span>` : nothing}
          ${this.snapshot?.barcode ? html`<span><b>${strings.eanLabel}</b> ${this.snapshot.barcode}</span>` : nothing}
        </div>
      </div>
      <span class="op-quick-add__price">${this.priceText}</span>
      <label class="op-quick-add__qty">
        <span>${strings.quantityLabel}</span>
        <input type="number" class="input" min=${rule.min || 1} step=${rule.increment || 1} max=${rule.max ?? ''} .value=${String(this.quantity)} inputmode="numeric" ?disabled=${disabled} aria-label=${interpolate(strings.quantityFor, { title: product.title })} @input=${this.handleQuantityInput} @change=${this.handleQuantityChange} @keydown=${this.handleQuantityKeydown}>
      </label>
      <button type="button" class=${classMap({ btn: true, 'btn--primary': true, 'btn--sm': true, 'op-quick-add__btn': true, 'is-loading': this.busy })} ?disabled=${disabled} @click=${this.add}>${this.added ? strings.added : strings.add}</button>
      ${reason || this.note ? html`<span class="op-quick-add__note">${reason || this.note}</span>` : nothing}
    `;
  }
}

if (!customElements.get('local-cart-search-row')) customElements.define('local-cart-search-row', LocalCartSearchRow);

window.LocalCartSearch = {
  enabled: () => Boolean(window.LocalCart?.isReady?.()),
  renderRow: renderRowTag,
  wrapResults: (rows, count) => wrapQuickAddResults(rows, count, window.theme.strings.opSearch),
  hydrate
};
